import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import SharedButton from '../../components/Shared/SharedButton';
import SearchField from '../../components/Shared/SearchField';
import DropdownComponent from '../../components/Shared/DropdownComponent';
import CustomTable from '../../components/Shared/CustomTable';
import CustomPagination from '../../components/Shared/CustomPagination';
import TableLoading from '../../components/Shared/TableLoading';
import { getERPClient } from '../../services/supplyChainApi';
import { DeleteIcon, EditIcon, EyeIcon } from '../../components/Shared/TableActionIcons';
import SharedConfirmDialog from '../../components/Shared/SharedConfirmDialog';
import toast from 'react-hot-toast';

export default function ERPItemsMain() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const erpClient = getERPClient();
      const params = {
        q: search.trim() || undefined,
        limit: 100,
        offset: 0,
      };

      const res = await erpClient.getItems(params);
      let list = Array.isArray(res) ? res : (res?.data || res?.items || []);
      if (categoryFilter !== 'ALL') {
        list = list.filter((i) => i.category_code === categoryFilter);
      }
      if (statusFilter === 'ACTIVE') {
        list = list.filter((i) => i.is_active);
      } else if (statusFilter === 'INACTIVE') {
        list = list.filter((i) => !i.is_active);
      }
      setItems(list);
    } catch (error) {
      console.error('Failed to fetch items:', error);
      toast.error(t('Failed to load ERP items'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter, t]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleEdit = (item) => {
    navigate(`/manager/erp/items/edit/${item.id || item.item_code}`, { state: { item } });
  };

  const handleCreate = () => {
    navigate('/manager/erp/items/create');
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setConfirmVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      const erpClient = getERPClient();
      await erpClient.deleteItem(itemToDelete.id || itemToDelete.item_code);
      toast.success(t('Item deleted successfully'));
      setItemToDelete(null);
      setConfirmVisible(false);
      fetchItems();
    } catch (error) {
      console.error('Failed to delete item:', error);
      const msg = error.response?.data?.message || t('Failed to delete item');
      toast.error(msg);
      setConfirmVisible(false);
    }
  };

  const statusBodyTemplate = (rowData) => {
    return rowData.is_active ? (
      <Tag value="Active" severity="success" />
    ) : (
      <Tag value="Inactive" severity="danger" />
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex items-center gap-2">
        <EyeIcon
          onClick={() => {
            setSelectedItem(rowData);
            setDetailVisible(true);
          }}
          title={t('View Details')}
        />
        <EditIcon onClick={() => handleEdit(rowData)} />
        <DeleteIcon title={t('Delete')} onClick={() => handleDeleteClick(rowData)} />
      </div>
    );
  };

  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1 + (page - 1) * perPage;
  };

  const categoryOptions = [
    { label: t('All Categories'), value: 'ALL' },
    { label: t('MEDICINE'), value: 'MEDICINE' },
    { label: t('CONSUMABLES'), value: 'CONSUMABLES' },
    { label: t('SURGICAL'), value: 'SURGICAL' },
    { label: t('LAB_REAGENT'), value: 'LAB_REAGENT' },
    { label: t('EQUIPMENT'), value: 'EQUIPMENT' },
  ];

  const statusOptions = [
    { label: t('All Statuses'), value: 'ALL' },
    { label: t('Active Only'), value: 'ACTIVE' },
    { label: t('Inactive Only'), value: 'INACTIVE' },
  ];

  const dash = (v) => (v === null || v === undefined || v === '' ? '-' : v);

  const columnsData = [
    { body: actionBodyTemplate, header: t('actions'), style: { width: '130px' } },
    { body: sequenceBodyTemplate, header: t('no'), style: { width: '3rem' } },
    { field: 'item_code', header: t('Item Code'), style: { width: '130px' } },
    { body: (row) => dash(row.name_en || row.name), field: 'name_en', header: t('Item Name (EN)') },
    { body: (row) => dash(row.item_type), field: 'item_type', header: t('Item Type'), style: { width: '110px' } },
    { body: (row) => dash(row.category_name_en || row.category_code || row.category_name), field: 'category_name_en', header: t('Category'), style: { width: '150px' } },
    { body: (row) => dash(row.generic_name_en), field: 'generic_name_en', header: t('Generic Name'), style: { width: '130px' } },
    { body: (row) => dash(row.trade_name_en), field: 'trade_name_en', header: t('Trade Name'), style: { width: '130px' } },
    { body: (row) => dash(row.gtin_14), field: 'gtin_14', header: t('GTIN 14'), style: { width: '130px' } },
    { body: (row) => dash(row.sfda_registration_no), field: 'sfda_registration_no', header: t('SFDA Reg No'), style: { width: '130px' } },
    { body: (row) => dash(row.description_en), field: 'description_en', header: t('Description') },
    { body: statusBodyTemplate, field: 'is_active', header: t('Status'), style: { width: '90px' } },
  ];

  const paginatedData = items.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(items.length / perPage) || 1;

  const handlePageChange = (newPage, newPerPage) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  };

  return (
    <div>
      <div className="flex flex-col mb-2 md:flex-row justify-between items-center gap-3">
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchField
            className="w-full md:w-[280px]"
            placeholder={t('Search by Code, Name, Barcode...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            handleClearClick={() => setSearch('')}
          />
          <div className="w-full sm:w-[180px]">
            <DropdownComponent
              useFormik={false}
              value={categoryFilter}
              handleChange={(e) => setCategoryFilter(e.value)}
              options={categoryOptions}
              placeholder={t('Category Filter')}
            />
          </div>
          <div className="w-full sm:w-[180px]">
            <DropdownComponent
              useFormik={false}
              value={statusFilter}
              handleChange={(e) => setStatusFilter(e.value)}
              options={statusOptions}
              placeholder={t('Status Filter')}
            />
          </div>
        </div>

        <SharedButton
          label={t('Create Item')}
          icon="pi pi-plus"
          onClick={handleCreate}
          buttonType="primary"
        />
      </div>

      <CustomTable
        data={paginatedData}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={columnsData}
        rows={perPage}
      />

      {items.length > 0 && (
        <CustomPagination
          currentPage={page}
          rowsPerPage={perPage}
          totalRecords={totalPages}
          totalRecord={items.length}
          onPageChange={handlePageChange}
        />
      )}

      <SharedConfirmDialog
        visible={confirmVisible}
        setVisible={setConfirmVisible}
        header="Delete Item"
        message={`${t('Are you sure you want to delete this item?')} ${itemToDelete?.name || itemToDelete?.item_code || ''}`}
        rejectLabel="Cancel"
        acceptLabel="Delete"
        onAccept={handleDeleteConfirm}
        onReject={() => setItemToDelete(null)}
      />

      <Dialog
        header={t('Item Details')}
        visible={detailVisible}
        onHide={() => setDetailVisible(false)}
        pt={{
          root: { className: 'w-full lg:w-9/12 mx-4' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-4' },
          closeButton: { className: 'hover:!bg-black/20 !outline-none !shadow-none' },
          closeButtonIcon: { className: '!text-white' },
        }}
      >
        {selectedItem && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 bg-gray-50 p-3 rounded border">
              <div>
                <span className="text-xs text-gray-500 block">{t('Item Code')}</span>
                <span className="font-semibold">{selectedItem.item_code || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Item Name (EN)')}</span>
                <span>{selectedItem.name_en || selectedItem.name || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Item Name (AR)')}</span>
                <span>{selectedItem.name_ar || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Item Type')}</span>
                <span>{selectedItem.item_type || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Category')}</span>
                <span>{selectedItem.category_name_en || selectedItem.category_code || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Stock UOM')}</span>
                <span>{selectedItem.stock_uom_id || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Generic Name')}</span>
                <span>{selectedItem.generic_name_en || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Trade Name')}</span>
                <span>{selectedItem.trade_name_en || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('GTIN 14')}</span>
                <span>{selectedItem.gtin_14 || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('SFDA Reg No')}</span>
                <span>{selectedItem.sfda_registration_no || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Status')}</span>
                {statusBodyTemplate(selectedItem)}
              </div>
            </div>
            {selectedItem.description_en && (
              <div className="bg-gray-50 p-3 rounded border">
                <span className="text-xs text-gray-500 block mb-1">{t('Description')}</span>
                <span>{selectedItem.description_en}</span>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}
